import { useState, type FormEvent } from "react";
import { resonancePosts } from "@/mocks/quotes";

interface Post {
  id: string;
  name: string;
  text: string;
  likes: number;
  date: string;
}

const initialPosts: Post[] = resonancePosts.map((p) => ({ ...p }));

export default function ResonanceWall() {
  const [posts, setPosts] = useState<Post[]>(initialPosts);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [notice, setNotice] = useState("");

  const toggleLike = (id: string) => {
    const isLiked = Boolean(liked[id]);
    setLiked((prev) => ({ ...prev, [id]: !isLiked }));
    setPosts((list) =>
      list.map((p) => (p.id === id ? { ...p, likes: p.likes + (isLiked ? -1 : 1) } : p)),
    );
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) {
      setNotice("先写点什么再发布吧。");
      return;
    }
    setPosts((list) => [
      {
        id: `local-${Date.now()}`,
        name: name.trim() || "路过的你",
        text: trimmed,
        likes: 0,
        date: "刚刚",
      },
      ...list,
    ]);
    setName("");
    setText("");
    setNotice("已添加到共鸣墙（仅保存在你的当前页面，刷新后会恢复）。");
  };

  return (
    <section id="wall" className="w-full bg-background-100 py-16 md:py-24 scroll-mt-16">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8">
        <div className="max-w-2xl">
          <span className="text-xs tracking-widest text-secondary-700 uppercase">Resonance</span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
            共鸣墙：原来有这么多人和你一样
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
            这些是读者留下的心情片段。你可以给说中心事的某一条点一个「共鸣」，
            也可以在这里写下今晚的心情——内容只留在当前页面，不会被上传。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {posts.map((p) => {
              const isLiked = liked[p.id];
              return (
                <article
                  key={p.id}
                  className="flex flex-col justify-between rounded-lg bg-background-50 border border-background-200/70 p-5"
                >
                  <p className="text-sm leading-relaxed text-foreground-800">{p.text}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                        {p.name.slice(0, 1)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground-900 truncate">{p.name}</p>
                        <p className="text-xs text-foreground-500">{p.date}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleLike(p.id)}
                      aria-pressed={isLiked}
                      aria-label={isLiked ? "取消共鸣" : "表示共鸣"}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        isLiked
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
                      }`}
                    >
                      <i className={isLiked ? "ri-heart-fill" : "ri-heart-line"}></i>
                      {p.likes}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="rounded-lg bg-background-50 border border-background-200/70 p-6 h-fit lg:sticky lg:top-24">
            <h3 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-quill-pen-line text-primary-600"></i>
              写下今晚的心情
            </h3>
            <form className="mt-5 flex flex-col gap-3" onSubmit={handleSubmit} noValidate>
              <label className="text-xs text-foreground-600" htmlFor="wall-name">
                你的称呼（可选）
              </label>
              <input
                id="wall-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例如：凌晨的猫"
                className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />

              <label className="text-xs text-foreground-600 mt-1" htmlFor="wall-text">
                想说的话
              </label>
              <textarea
                id="wall-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={200}
                rows={4}
                placeholder="也许只有一句，但它是真的。"
                className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">{text.length} / 200</span>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i>
                  发布到共鸣墙
                </button>
              </div>
              {notice && (
                <p className="text-xs text-accent-800 bg-accent-50 border border-accent-100 rounded-md px-3 py-2">
                  {notice}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}